import * as React from 'react';
import { Button, Modal, Spinner } from '@heroui/react';
import { Stepper } from '@heroui-pro/react';
import { IconChevronLeft } from '@tabler/icons-react';
import { ActivityFormProvider, useActivityFormContext } from './steps/ActivityForm';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export type ActivityEditorModalProps = {
  isOpen: boolean;
  /** 与 URL `?editor=` 一致：`new` 或活动 id */
  editorParam: string | null;
  /** 仅在弹层从关→开时递增，避免新建保存后换 id 时整树重挂载 */
  editorMountKey: number;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onPersistedActivityId: (id: string) => void;
};

function ActivityEditorModalHeader() {
  const { onClose, isEdit, step, handleStepChange, stepDefs } = useActivityFormContext();
  return (
    <div className="flex w-full shrink-0 flex-col gap-0">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-divider px-4">
        <Button variant="ghost" className="mr-1 shrink-0" onPress={() => onClose()}>
          <IconChevronLeft className="size-6" stroke={1.5} />
          返回
        </Button>
        <Modal.Heading className="min-w-0 flex-1 truncate text-2xl font-bold">
          {isEdit ? '编辑活动' : '新建活动'}
        </Modal.Heading>
      </div>
      <div className="shrink-0 border-b border-divider px-6 py-4">
        <Stepper
          className="w-full"
          currentStep={step - 1}
          onStepChange={(idx) => handleStepChange(idx + 1)}
          orientation="horizontal"
        >
          {stepDefs.map((s) => (
            <Stepper.Step key={s.step}>
              <Stepper.Indicator>{s.step}</Stepper.Indicator>
              <Stepper.Content className="flex flex-col items-start gap-0.5">
                <Stepper.Title className="text-sm font-medium">{s.title}</Stepper.Title>
                <Stepper.Description className="text-muted-foreground text-xs">
                  {s.description}
                </Stepper.Description>
              </Stepper.Content>
              <Stepper.Separator className="md:mx-2.5" />
            </Stepper.Step>
          ))}
        </Stepper>
      </div>
    </div>
  );
}

function ActivityEditorModalFooter() {
  const { step, onClose, isSubmitting, submitIntentRef, canEnterStep3, handleStepChange, setStep } =
    useActivityFormContext();
  return (
    <div className="flex w-full flex-wrap items-center justify-start gap-2">
      {step === 1 ? (
        <>
          <Button type="button" variant="secondary" isDisabled={isSubmitting} onPress={() => onClose()}>
            取消
          </Button>
          <Button
            type="submit"
            form="activity-editor-form"
            isDisabled={isSubmitting}
            onPress={() => {
              submitIntentRef.current = 'draft';
            }}
          >
            {isSubmitting && submitIntentRef.current === 'draft' ? (
              <Spinner className="mr-2 size-4" />
            ) : null}
            存为草稿
          </Button>
          <Button
            type="submit"
            form="activity-editor-form"
            isDisabled={isSubmitting}
            onPress={() => {
              submitIntentRef.current = 'published';
            }}
          >
            {isSubmitting && submitIntentRef.current === 'published' ? (
              <Spinner className="mr-2 size-4" />
            ) : null}
            立即发布
          </Button>
        </>
      ) : (
        <>
          <Button type="button" variant="secondary" onPress={() => setStep((prev) => Math.max(1, prev - 1))}>
            上一步
          </Button>
          {step === 2 ? (
            <Button type="button" isDisabled={!canEnterStep3} onPress={() => handleStepChange(3)}>
              下一步：抽奖规则
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * HeroUI 全幅活动编辑器：`size="cover"`，标题与步骤条在 {@link Modal.Header}，操作在 {@link Modal.Footer}
 */
export function ActivityEditorModal({
  isOpen,
  editorParam,
  editorMountKey,
  onOpenChange,
  onClose,
  onPersistedActivityId,
}: ActivityEditorModalProps) {
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="cover">
          <Modal.Dialog>
            {isOpen && editorParam ? (
              <ActivityFormProvider
                key={editorMountKey}
                activityId={editorParam === 'new' ? null : editorParam}
                onClose={onClose}
                onPersistedActivityId={onPersistedActivityId}
              >
                {({ FormBody }) => (
                  <>
                    <Modal.Header>
                      <ActivityEditorModalHeader />
                    </Modal.Header>
                    <Modal.Body className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                      <FormBody />
                    </Modal.Body>
                    <Modal.Footer>
                      <ActivityEditorModalFooter />
                    </Modal.Footer>
                  </>
                )}
              </ActivityFormProvider>
            ) : null}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
